/**
 * One job, open.
 *
 * Mounted with `key={reference}`, so opening a second job cannot inherit the
 * first one's verdicts, typed prices or entered hours. That is not a
 * theoretical tidiness point: those three are exactly the state that would
 * silently move money from one customer's estimate onto another's.
 *
 * Everything it needs is loaded here rather than passed down, so the job screen
 * is a unit — it either has what it needs or it says what is missing.
 */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSession } from './SessionProvider.js';
import { useJob } from './useJob.js';
import { TopBar, ShellFooter } from './TopBar.js';
import { useAsync, useRepo, useUsingFixtures } from '../data/RepoProvider.js';
import { Loading, Failed } from '../components/Loading.js';
import { Capture } from '../screens/Capture.js';
import { Review } from '../screens/Review.js';
import { AskPanel } from '../screens/AskPanel.js';
import { Price } from '../screens/Price.js';
import { Issue } from '../screens/Issue.js';
import type { CapturedPhoto } from '../capture/photos.js';
import type { UseUploadQueue } from '../capture/useUploadQueue.js';
import type { JobDetail } from '../data/repo.js';
import type { CatalogEntry, ExclusionRule } from '../estimate/build.js';
import type { RateProfile } from '../calc/types.js';
import { displayStage, type Stage } from './jobStage.js';

const STAGE_ORDER: Stage[] = ['capture', 'review', 'price', 'issue'];

type JobTab = 'capture' | 'review' | 'price' | 'issue';

interface Props {
  reference: string;
  onBack: () => void;
  queue: UseUploadQueue;
  /** Leave the job and open the catalog on this part. */
  onOpenCatalog?: (partName: string) => void;
}

export function JobShell({ reference, onBack, queue, onOpenCatalog }: Props) {
  const repo = useRepo();
  // Bumped after an analysis so the new proposals are actually fetched. Without
  // it the model would write rows the screen never asks for again.
  const [revision, setRevision] = useState(0);
  // `tab` and `analysed` live HERE, above the keyed workspace. Bumping the
  // revision remounts JobWorkspace to clear the previous job's verdicts — and
  // a remount would reset anything held inside it. That is exactly what
  // happened after an analysis: the proposals arrived, the screen jumped back
  // to Capture, and the count of what was found vanished with it.
  const [tab, setTab] = useState<JobTab>('capture');
  const [analysed, setAnalysed] = useState<number | null>(null);
  const detail = useAsync(() => repo.getJob(reference), [repo, reference, revision]);
  const [catalogRevision, setCatalogRevision] = useState(0);
  const catalog = useAsync(() => repo.listCatalog(), [repo, catalogRevision]);
  const exclusions = useAsync(() => repo.listExclusions(), [repo]);
  const profile = useAsync(() => repo.getRateProfile(), [repo]);

  const backNav = (
    <button className="iconbtn teal" onClick={onBack} title="Back to Jobs" aria-label="Back to Jobs">
      {'‹'}
    </button>
  );

  for (const [what, r] of [
    ['this job', detail], ['the catalog', catalog],
    ['the exclusion rules', exclusions], ['the rates', profile],
  ] as const) {
    if (r.state === 'error') {
      return (
        <div className="shell">
          <TopBar nav={backNav} />
          <div className="body wide"><main className="shell-main">
            <Failed what={what} message={r.message} />
          </main></div>
        </div>
      );
    }
  }
  if (detail.state !== 'ready' || catalog.state !== 'ready'
      || exclusions.state !== 'ready' || profile.state !== 'ready') {
    return (
      <div className="shell">
        <TopBar nav={backNav} />
        <div className="body wide"><main className="shell-main">
          <Loading what={`job ${reference}`} />
        </main></div>
      </div>
    );
  }
  if (detail.value === null) {
    return (
      <div className="shell">
        <TopBar nav={backNav} />
        <div className="body wide"><main className="shell-main">
          <div className="note warn">
            <b>No job {reference}</b>
            It may belong to another shop, or have been removed. Nothing is wrong
            with your account.
          </div>
        </main></div>
      </div>
    );
  }

  return (
    <JobWorkspace
      key={revision}
      job={detail.value} catalog={catalog.value} exclusions={exclusions.value}
      profile={profile.value} onBack={onBack} queue={queue}
      tab={tab} setTab={setTab} analysed={analysed}
      onCatalogChanged={() => setCatalogRevision((n) => n + 1)}
      onOpenCatalog={onOpenCatalog}
      onAnalysed={(added) => { setAnalysed(added); setRevision((n) => n + 1); }}
    />
  );
}

function JobWorkspace({ job: detail, catalog, exclusions, profile, onBack, queue,
                       tab, setTab, analysed, onAnalysed,
                       onCatalogChanged, onOpenCatalog }: {
  job: JobDetail;
  catalog: CatalogEntry[];
  exclusions: ExclusionRule[];
  profile: RateProfile;
  onBack: () => void;
  queue: UseUploadQueue;
  tab: JobTab;
  setTab: (t: JobTab) => void;
  /** A price was written to the catalog; refetch it for this job too. */
  onCatalogChanged: () => void;
  onOpenCatalog?: (partName: string) => void;
  analysed: number | null;
  onAnalysed: (added: number) => void;
}) {
  const { session, t, money } = useSession();
  const usingFixtures = useUsingFixtures();
  const repo = useRepo();
  // The ask panel lives with the job, so it carries the reference and closes
  // when you leave. It is deliberately not global: "ask about this estimate"
  // means nothing on the jobs list.
  const [asking, setAsking] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [sentTo, setSentTo] =
    useState<{ sentTo: string; link: string; emailed: boolean } | null>(
      // A link that went out in an earlier session still counts as sent. The
      // link itself is deliberately absent: only its hash is stored, so it
      // cannot be shown again — a resend mints a fresh one.
      detail.approvalSentTo
        ? { sentTo: detail.approvalSentTo, link: '', emailed: true }
        : null);
  // What they wrote when they approved only part of it — the only place the
  // shop learns which part.
  const [resending, setResending] = useState(false);
  const [customerNote, setCustomerNote] =
    useState<string | null>(detail.approval?.note ?? null);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [analysing, setAnalysing] = useState(false);
  const [analyseError, setAnalyseError] = useState<string | null>(null);
  // 'auto' lets the server decide (its own default). A concrete choice here is
  // deliberately NOT remembered anywhere - the whole point of exposing it is
  // to compare providers on the SAME photographs, which means picking fresh
  // each time rather than a setting nobody revisits.
  const [readProvider, setReadProvider] = useState<'auto' | 'gemini' | 'anthropic'>('gemini');
  const [readWith, setReadWith] = useState<{ provider: string; model: string } | null>(null);

  /**
   * Persist an overrule of the model on repair-versus-replace.
   *
   * Only the write — useJob owns the state change and calls this. That split is
   * what lets it be declared above useJob without a temporal dead zone, and it
   * means Review and Price cannot diverge: both go through useJob.setOperation.
   *
   * The draft alone kept the estimate correct but threw the LABEL away, and the
   * label — model said X, an expert said Y, on these photographs — is the part
   * worth keeping.
   */
  const overrule = useCallback((key: string, operation: string) => {
    repo.recordOperation(detail.reference, key, operation)
      .then(() => setSaveError(null))
      .catch((err: unknown) => setSaveError(
        err instanceof Error ? err.message : String(err)));
  }, [repo, detail.reference]);

  const job = useJob({
    impact: detail.impact,
    initialShots: detail.shots,
    candidates: detail.candidates,
    // Decisions already made, so reopening a job does not ask again.
    initialVerdicts: detail.verdicts,
    // Prices, hours and hand lines from the last sitting.
    initialDraft: detail.draft,
    initialIssued: detail.issued,
    initialAnswer: detail.approval?.choice ?? null,
    onOperationOverride: overrule,
    catalog,
    exclusions,
    profile,
    acv: detail.acv ?? undefined,
    bodyStyle: detail.bodyStyle,
    make: detail.make,
  });

  // Blocked proposals never get a verdict — Review cannot be "done" while one
  // is still waiting on a photograph nobody can take yet. Excluding them here
  // matches the gate itself, not a looser count that would strand this step
  // at "current" forever.
  const decidable = job.proposals.filter((p) => !p.blocked);
  const pending = decidable.filter((p) => !job.verdicts[`${p.zone}::${p.partLabel}`]).length;
  const missingPrices = job.prompts.filter((p) => p.kind === 'missing_price').length;

  const stage = displayStage({
    shotsTaken: job.gaps.taken, shotsTotal: job.list.length,
    proposalsTotal: decidable.length, proposalsDecided: decidable.length - pending,
    missingPrices, issued: Boolean(job.issued),
  });
  const stageAt = STAGE_ORDER.indexOf(stage);

  // Land on wherever the job actually is, not always Capture — a job that
  // already has proposals sitting in Review (or further) shouldn't make the
  // estimator click past a step that's done every time they reopen it. Once
  // only, per job: after that, `tab` is the estimator's own navigation, and
  // reviewing an earlier step on purpose must not keep getting overridden.
  const landedRef = useRef(false);
  useEffect(() => {
    if (landedRef.current) return;
    landedRef.current = true;
    if (stage !== 'capture') setTab(stage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A verdict is the audit record behind the acceptance rate, so it is written
  // as it is made rather than at the end. Losing the tab must not lose it.
  //
  // A failure here is NOT swallowed. The screen would otherwise show a decision
  // that was never stored — the estimator moves on believing it is kept, and
  // finds it undecided tomorrow with no idea why.
  const [saveError, setSaveError] = useState<string | null>(null);

  /**
   * Save the working draft when the edits settle.
   *
   * Debounced, because this fires on every keystroke in a price field and a
   * write per character would be both wasteful and slower than typing. The
   * first render is skipped: it would immediately write back the draft it had
   * just loaded, and on an issued job that is a pointless write to a document
   * nobody may change.
   */
  const [draftState, setDraftState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const firstDraft = useRef(true);
  useEffect(() => {
    if (firstDraft.current) { firstDraft.current = false; return; }
    if (job.issued) return;
    setDraftState('saving');
    const timer = setTimeout(() => {
      repo.saveDraft(detail.reference, job.draft)
        .then(() => setDraftState('saved'))
        .catch(() => setDraftState('failed'));
    }, 700);
    return () => clearTimeout(timer);
  }, [job.draft, job.issued, repo, detail.reference]);
  const decide = useCallback((key: string, verdict: Parameters<typeof job.decide>[1]) => {
    job.decide(key, verdict);
    repo.recordVerdict(detail.reference, key, verdict)
      .then(() => setSaveError(null))
      .catch((err: unknown) => setSaveError(
        err instanceof Error ? err.message : String(err)));
  }, [job, repo, detail.reference]);


  /**
   * Ask the model to read the photographs.
   *
   * Everything else in the app was built around this call and nothing ever made
   * it. On success the job is refetched — the proposals live in the database,
   * not in this component — and the estimator is moved to Review, because that
   * is unambiguously the next thing they do.
   */
  const analyse = useCallback(async () => {
    setAnalysing(true);
    setAnalyseError(null);
    try {
      const out = await repo.analysePhotos(
        detail.reference, readProvider === 'auto' ? undefined : readProvider);
      if (out === null) {
        setAnalyseError('There are no photographs on this job yet.');
        return;
      }
      setReadWith({ provider: out.provider, model: out.model });
      // Order matters: move to Review FIRST, because onAnalysed remounts this
      // component and anything set afterwards would be lost with it.
      setTab('review');
      onAnalysed(out.added);
    } catch (err) {
      setAnalyseError(err instanceof Error ? err.message : String(err));
    } finally {
      setAnalysing(false);
    }
  }, [repo, detail.reference, readProvider, onAnalysed, setTab]);

  /**
   * Issue: the database first, the screen second.
   *
   * The version number and document hash are the SERVER's. Freezing locally and
   * writing afterwards would let a customer be shown a version that failed to
   * save — and an approval against a version the database has never heard of is
   * worth nothing.
   */
  const doIssue = useCallback(async () => {
    setIssuing(true);
    setIssueError(null);
    try {
      const { version, hash } = await repo.issue(
        detail.reference, job.totals.grandTotal, job.lines, job.totals,
        session?.country.currencyCode ?? '');
      job.issue(version, hash);

      // Email the customer, but only when their record says they must answer.
      // A counter customer gets the estimate without being asked to approve
      // it, which is the fork the customer record already decides (FR35).
      if (detail.customer.needsApproval) {
        try {
          const sent = await repo.sendForApproval(detail.reference);
          setSentTo(sent);
        } catch (err) {
          // The version IS issued and frozen — that succeeded. Only the email
          // failed, so say exactly that rather than implying the issue failed.
          setIssueError(
            `Issued, but the email did not go out: ${
              err instanceof Error ? err.message : String(err)}`);
        }
      }
    } catch (err) {
      setIssueError(err instanceof Error ? err.message : String(err));
    } finally {
      setIssuing(false);
    }
  }, [repo, detail.reference, job, session]);

  /**
   * Watch for the customer's answer while the job is waiting on one.
   *
   * Polled rather than pushed: a shop looks at this screen a few times a day,
   * and a websocket for something that happens once per job would be a lot of
   * machinery to save sixty seconds. Stops the moment an answer arrives, and
   * never runs at all on a job that is not waiting.
   */
  useEffect(() => {
    if (!job.issued || job.answer || !detail.customer.needsApproval) return;
    let live = true;
    const check = () => {
      repo.approvalStatus(detail.reference)
        .then((res) => {
          if (!live || !res) return;
          job.setAnswer(res.choice);
          setCustomerNote(res.note);
        })
        .catch(() => { /* a failed poll is not worth interrupting anyone over */ });
    };
    check();
    const timer = setInterval(check, 60_000);
    return () => { live = false; clearInterval(timer); };
  }, [job.issued, job.answer, detail.customer.needsApproval, detail.reference, repo, job]);

  const capture = useCallback((shotId: string, photo?: CapturedPhoto) => {
    job.capture(shotId, photo);
    if (!photo || usingFixtures) return;
    // The blob is fetched back from the object URL the card is already showing,
    // so the queue holds the same bytes the estimator can see.
    void fetch(photo.url).then((r) => r.blob()).then((blob) => queue.enqueue({
      id: `${detail.reference}:${shotId}:${photo.takenAt}`,
      jobId: detail.id,
      shopId: session?.shop.id ?? '',
      slot: shotId,
      blob,
      width: photo.width,
      height: photo.height,
      takenAt: photo.takenAt,
    }));
  }, [job, queue, detail, session, usingFixtures]);

  if (!session) return null;
  const { can } = session;

  const tabs: Array<[JobTab, string]> = [
    ['capture', t('Capture')], ['review', t('Review')], ['price', t('Price')], ['issue', t('Issue')],
  ];

  // What each step actually shows, whether it's behind, current or ahead —
  // one sentence answering "why am I on this step" or "why can't I get to
  // the next one yet". Every count already exists above; this only phrases it.
  const shotsLeft = job.list.length - job.gaps.taken;
  const stepCaptions: string[] = [
    stageAt > 0 ? `All ${job.list.length} shots taken`
      : shotsLeft > 0 ? `${job.gaps.taken} of ${job.list.length} shots · ${shotsLeft} still missing`
      : `${job.gaps.taken} of ${job.list.length} shots · ready to read`,
    stageAt > 1 ? 'All proposals decided'
      : stageAt === 1 ? `${decidable.length - pending} of ${decidable.length} decided · ${pending} pending your verdict`
      : 'waiting on Capture',
    stageAt > 2 ? 'Priced'
      : stageAt === 2 ? `${missingPrices} part${missingPrices === 1 ? '' : 's'} still need a price`
      : 'waiting on Review',
    job.issued ? `Issued · v${job.issued.version}`
      : stageAt === 3 ? 'Ready to issue'
      : 'waiting on Price',
  ];

  return (
    <div className="shell">
      {/* One row: the job's identity on the left, the total on the right,
          and — centered between them, in its own tinted pill so it reads as
          a distinct thing rather than more bar — the step nav. Which steps
          are done/current/upcoming (stageAt) is what the job has actually
          reached; which one is on screen right now (`tab`) is layered on
          top of that, so browsing back to a finished step never reads as
          having undone it. */}
      <TopBar nav={
        <button className="iconbtn teal" onClick={onBack} title={t('Jobs')} aria-label={t('Jobs')}>
          {'‹'}
        </button>
      } />

      <div className="jobbar">
        <div className="jobbar-info">
          {/* Said out loud. An estimator who has just watched a screen lose
              their prices needs to see that this one is keeping them. */}
          {draftState !== 'idle' && !job.issued && (
            <span className={`draftstate ${draftState}`}>
              {draftState === 'saving' ? 'Saving…'
                : draftState === 'saved' ? '✓ Saved'
                : '⚠ Not saved — check your connection'}
            </span>
          )}
          <span className="mono">{detail.reference}</span><span>·</span>
          <b>{detail.customer.name}</b>
          <span className={`flag ${detail.customer.needsApproval ? 'need' : 'auto'}`}>
            {detail.customer.needsApproval ? t('Needs approval') : t('Auto-approve')}
          </span>
          {detail.vehicle && <><span>·</span><b>{detail.vehicle}</b></>}
          {detail.impact && <><span>·</span><span>{detail.impact.replace(/_/g, ' ')}</span></>}
        </div>

        <div className="jobsteps">
          {tabs.map(([id, label], i) => (
            <Fragment key={id}>
              {i > 0 && <div className={`jhline${i <= stageAt ? ' done' : ''}`} />}
              <button type="button" aria-current={tab === id} onClick={() => setTab(id)}
                      className={`jhstep${i < stageAt ? ' done' : i === stageAt ? ' current' : ' upcoming'}${
                        tab === id ? ' onscreen' : ''}`}>
                <span className="jhdot">{i < stageAt ? '✓' : i + 1}</span>
                <span className="jhbody">
                  <b>{label}</b>
                  <span>{stepCaptions[i]}</span>
                </span>
              </button>
            </Fragment>
          ))}
        </div>

        <div className="jobbar-right">
          {job.issued && <span className="flag lock">{'\u{1F512}'} v1</span>}
          {can.seePrices && <span className="total">{money(job.totals.grandTotal)}</span>}
        </div>
      </div>

      <div className="body">
        <main className="shell-main">
          {tab === 'capture' && (
            <Capture impact={detail.impact}
                     states={job.shots} photos={job.photos} onCapture={capture}
                     queue={{ summary: queue.summary, online: queue.online }}
                     onAnalyse={() => void analyse()} analysing={analysing}
                     analyseError={analyseError} analysed={analysed}
                     readProvider={readProvider} onReadProviderChange={setReadProvider}
                     readWith={readWith}
                     extraShots={detail.extraShots}
                     photoUrls={detail.photoUrls}
                     onRemove={async (slotId) => {
                       await repo.removePhoto(detail.reference, slotId);
                       // Refetch: the slot, its evidence links and any
                       // proposal that cited it have all changed underneath.
                       onAnalysed(analysed ?? 0);
                     }} />
          )}
          {tab === 'review' && (
            <Review proposals={job.proposals} verdicts={job.verdicts}
                    onVerdict={decide} rate={job.rate} locked={Boolean(job.issued)}
                    cannotAssess={detail.cannotAssess}
                    onPhotograph={() => setTab('capture')}
                    photoUrls={detail.photoUrls}
                    takenSlots={job.shots.filter((sh) => sh.status === 'taken')
                      .map((sh) => sh.id)}
                    saveError={saveError}
                    onOperation={job.setOperation} />
          )}
          {tab === 'price' && (
            <Price job={job} profile={profile} onOpenCatalog={onOpenCatalog}
                   onSaveToCatalog={async (entry) => {
                     await repo.addCatalogEntry(entry);
                     // Refetch so the catalog this job prices from now holds it
                     // too, rather than only the copy in the estimator's head.
                     onCatalogChanged();
                   }} />
          )}
          {tab === 'issue' && (
            <Issue job={job} customer={detail.customer}
                   vehicle={detail.vehicle} reference={detail.reference}
                   onIssue={() => void doIssue()} issuing={issuing} sent={sentTo}
                   customerNote={customerNote}
                   resending={resending}
                   staleApproval={detail.staleApproval}
                   onResend={() => {
                     setResending(true);
                     setIssueError(null);
                     repo.sendForApproval(detail.reference)
                       .then(setSentTo)
                       .catch((err: unknown) => setIssueError(
                         err instanceof Error ? err.message : String(err)))
                       .finally(() => setResending(false));
                   }}
                   issueError={issueError} />
          )}
        </main>

        {can.seePrices && (
          <aside className="rail">
            <h4>{t('Totals')}</h4>
            <div className="kv"><span>{t('Parts')}</span>
              <span>{money(job.totals.partsTotal)}</span></div>
            <div className="kv"><span>Body labour</span>
              <span>{money(job.totals.labour.body.amount)}</span></div>
            <div className="kv"><span>Refinish</span>
              <span>{money(job.totals.labour.refinish.amount)}</span></div>
            <div className="kv"><span>Mechanical</span>
              <span>{money(job.totals.labour.mechanical.amount)}</span></div>
            <div className="kv"><span>Paint materials</span>
              <span>{money(job.totals.paintMaterials)}</span></div>
            <div className="kv"><span>Shop materials</span>
              <span>{money(job.totals.shopMaterials)}</span></div>
            {job.totals.taxes.map((tx) => (
              <div className="kv" key={tx.name}>
                <span>{tx.name} {tx.rate}%</span><span>{money(tx.amount)}</span>
              </div>
            ))}
            <div className="kv total"><span>{t('Total')}</span>
              <span>{money(job.totals.grandTotal)}</span></div>

            {job.totals.totalLoss && (
              <div className={`meter ${job.totals.totalLoss.percentOfLimit >= 80 ? 'crit'
                : job.totals.totalLoss.percentOfLimit >= 50 ? 'warn' : ''}`}>
                <h4>Total-loss meter</h4>
                <div className="track">
                  <div className="fill" style={{
                    width: `${Math.min(100, job.totals.totalLoss.percentOfLimit)}%` }} />
                </div>
                <div className="cap">
                  <span>{job.totals.totalLoss.percentOfLimit}% of limit</span>
                  <span>{money(job.totals.totalLoss.limit)}</span>
                </div>
              </div>
            )}

            <div className="rate">
              {job.rate.rate === null
                ? 'Acceptance — nothing decided yet'
                : `Acceptance ${job.rate.rate}% · ${job.rate.accepted}/${
                    job.rate.accepted + job.rate.rejected}`}
            </div>
            <div className="live">{'↻'} Recalculates on every edit</div>
          </aside>
        )}
      </div>

      {/* Anchored to the job, and only offered once there is something to ask
          about — an empty estimate has no questions worth the round trip. */}
      {asking
        ? <AskPanel reference={detail.reference} onClose={() => setAsking(false)} />
        : (job.proposals.length > 0 || job.lines.length > 0) && (
          <button className="askfab" onClick={() => setAsking(true)}>
            Ask about this estimate
          </button>
        )}

      <ShellFooter usingFixtures={usingFixtures} />
    </div>
  );
}
